import { type ReactNode, useCallback, useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import Badge from '@/components/UI/Badge';
import { ConfirmationDialog } from '@/components/UI';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type GetPlanOutput = RouterOutputs['agent']['autopilot']['getPlan'];
type PlanData = GetPlanOutput['plan'];
type Blueprint = PlanData['blueprint'];
type PreflightOutput = RouterOutputs['agent']['autopilot']['preflightBlueprint'];

interface AutopilotRunData {
    id: number;
    status: string;
    startedAt: string | null;
    finishedAt: string | null;
}

interface AutopilotPanelProps {
    planId: number;
    runData: AutopilotRunData | null;
    onNavigateToSession: (sessionId: number) => void;
}

type BadgeVariant = 'warning' | 'info' | 'success' | 'danger' | 'secondary';

const STATUS_BADGE_VARIANT: Record<string, BadgeVariant> = {
    draft: 'warning',
    locked: 'info',
    archived: 'secondary',
    running: 'success',
    completed: 'success',
    failed: 'danger',
    paused: 'secondary',
    canceled: 'secondary'
};

function CollapsibleSection({ title, defaultOpen = false, children }: { title: string; defaultOpen?: boolean; children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const id = useId();
    const panelId = `${id}-panel`;

    return (
        <div className="border-b border-zinc-800 last:border-b-0">
            <button
                aria-controls={panelId}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-zinc-200 hover:bg-zinc-800/40"
                type="button"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{title}</span>
                <span className="text-xs text-zinc-500">{isOpen ? '\u2212' : '+'}</span>
            </button>
            {isOpen && (
                <div className="px-3 pb-3 text-xs text-zinc-300" id={panelId} role="region">
                    {children}
                </div>
            )}
        </div>
    );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className="flex items-start gap-2 py-0.5">
            <span className="text-zinc-500 shrink-0 min-w-[100px]">{label}:</span>
            <span className="text-zinc-200 break-words">{value ?? <span className="text-zinc-600 italic">not set</span>}</span>
        </div>
    );
}

function ChipList({ items }: { items: string[] }) {
    return (
        <div className="flex flex-wrap gap-1 mt-0.5">
            {items.map((item) => (
                <span className="inline-block rounded bg-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-200" key={item}>
                    {item}
                </span>
            ))}
        </div>
    );
}

function BooleanDot({ value, label }: { value: boolean; label: string }) {
    return (
        <span className="inline-flex items-center gap-1">
            <span className={`inline-block h-2 w-2 rounded-full ${value ? 'bg-green-500' : 'bg-zinc-600'}`} />
            <span className={value ? 'text-zinc-200' : 'text-zinc-500'}>{label}</span>
        </span>
    );
}

function ObjectiveSection({ objective }: { objective: Blueprint['objective'] }) {
    return (
        <>
            <FieldRow label="Primary metric" value={objective.primaryMetric} />
            {objective.constraints && (
                <>
                    {objective.constraints.minTrades != null && <FieldRow label="Min trades" value={objective.constraints.minTrades} />}
                    {objective.constraints.minCoverage != null && (
                        <FieldRow label="Min coverage" value={`${(objective.constraints.minCoverage * 100).toFixed(0)}%`} />
                    )}
                    {objective.constraints.maxDrawdownPercent != null && (
                        <FieldRow label="Max drawdown" value={`${objective.constraints.maxDrawdownPercent}%`} />
                    )}
                </>
            )}
            {objective.tieBreakers && objective.tieBreakers.length > 0 && (
                <div className="mt-1">
                    <span className="text-zinc-500">Tiebreakers:</span>
                    <ChipList items={objective.tieBreakers} />
                </div>
            )}
        </>
    );
}

function ScopeSection({ scope }: { scope: Blueprint['scope'] }) {
    return (
        <>
            <FieldRow label="Instrument" value={scope.instrument} />
            <FieldRow
                label="Entity types"
                value={scope.entityTypes
                    .map((type) => (type === 'AssetIndex' ? 'Indexes' : type === 'Asset' ? 'Stocks / ETFs' : 'Cryptocurrency'))
                    .join(', ')}
            />
            <FieldRow label="Bar duration" value={scope.barDuration} />
            <FieldRow label="After-hours" value={scope.afterHoursRemoved ? 'Removed' : 'Included'} />
            {scope.entityTypes.includes('AssetIndex') && (
                <p className="mt-2 text-xs text-zinc-400">
                    Index movement supports research. Index option backtests must close before expiration; normal quote and spread checks
                    apply.
                </p>
            )}
        </>
    );
}

function UniverseSection({ universe }: { universe: Blueprint['universe'] }) {
    return (
        <>
            <FieldRow label="Type" value={universe.type} />
            {'symbols' in universe && <FieldRow label="Symbols" value={universe.symbols.join(', ')} />}
            {'entities' in universe && (
                <FieldRow
                    label="Entities"
                    value={universe.entities.map((entity) => `${entity.entityType}:${entity.entityId}`).join(', ')}
                />
            )}
            {'entityListId' in universe && <FieldRow label="Entity list ID" value={universe.entityListId} />}
            {'universeScreenerRuleId' in universe && <FieldRow label="Screener rule ID" value={universe.universeScreenerRuleId} />}
            {'maxEntities' in universe && universe.maxEntities != null && <FieldRow label="Max entities" value={universe.maxEntities} />}
        </>
    );
}

function StrategyDesignSection({ design }: { design: Blueprint['strategyDesign'] }) {
    return (
        <>
            <FieldRow label="Name template" value={design.strategyNameTemplate} />
            <FieldRow label="Legs" value={`${design.legs.length} (${design.legs.map((l) => `${l.side} ${l.kind}`).join(', ')})`} />
            <div className="mt-1.5 text-zinc-400 font-medium">Entry</div>
            <FieldRow label="Source" value={design.entry.source} />
            <FieldRow label="Template" value={design.entry.template} />
            {Object.keys(design.entry.params).length > 0 && (
                <FieldRow
                    label="Params"
                    value={Object.entries(design.entry.params)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                />
            )}
            <FieldRow
                label="Variations"
                value={
                    design.entry.variationPlan.enabled
                        ? `${design.entry.variationPlan.maxVariations} (${design.entry.variationPlan.method})`
                        : 'Disabled'
                }
            />
            <div className="mt-1.5 text-zinc-400 font-medium">Exit</div>
            <FieldRow label="Source" value={design.exit.source} />
            <FieldRow label="Template" value={design.exit.template} />
            {Object.keys(design.exit.params).length > 0 && (
                <FieldRow
                    label="Params"
                    value={Object.entries(design.exit.params)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                />
            )}
            <FieldRow
                label="Variations"
                value={
                    design.exit.variationPlan.enabled
                        ? `${design.exit.variationPlan.maxVariationsApplied} (${design.exit.variationPlan.generationMethod})`
                        : 'Disabled'
                }
            />
        </>
    );
}

function ExperimentPlanSection({ plan }: { plan: Blueprint['experimentPlan'] }) {
    const horizonLabels = plan.horizonsSeconds.map((s) => {
        if (s >= 86400) return `${Math.round(s / 86400)}d`;
        if (s >= 3600) return `${Math.round(s / 3600)}h`;
        return `${s}s`;
    });

    return (
        <>
            <FieldRow label="Date range" value={`${plan.dateRange.startEt} \u2192 ${plan.dateRange.endEt}`} />
            <FieldRow label="Horizons" value={horizonLabels.join(', ')} />
            <FieldRow label="Include options" value={plan.includeOptions ? 'Yes' : 'No'} />
            <div className="mt-1.5 flex flex-col gap-0.5">
                <BooleanDot label="Parameter discovery" value={plan.baseline.parameterDiscovery.enabled} />
                <BooleanDot
                    label={`Screener optimization (max ${plan.screenerOptimization.maxOptimizerJobs} jobs)`}
                    value={plan.screenerOptimization.enabled}
                />
                <BooleanDot
                    label={`Scorecard (${plan.scorecard.ruleBudget.min}-${plan.scorecard.ruleBudget.max} rules, ${plan.scorecard.mode})`}
                    value={plan.scorecard.enabled}
                />
                <BooleanDot
                    label={`Exit optimization (max ${plan.exitOptimization.maxIterations} iterations)`}
                    value={plan.exitOptimization.enabled}
                />
            </div>
        </>
    );
}

function BacktestPlanSection({ plan }: { plan: Blueprint['backtestPlan'] }) {
    return (
        <>
            <BooleanDot label="Strategy backtests" value={plan.populateStrategyBacktests.enabled} />
            <BooleanDot label="Portfolio" value={plan.portfolio.enabled} />
            {plan.portfolio.enabled && (
                <>
                    <FieldRow label="Deposit" value={`$${plan.portfolio.paper.depositUsd.toLocaleString()}`} />
                    <FieldRow label="Trading profile" value={plan.portfolio.tradingProfile.source} />
                    <BooleanDot label="Portfolio backtest" value={plan.portfolio.portfolioBacktest.run} />
                </>
            )}
        </>
    );
}

function BudgetsSection({ budgets }: { budgets: Blueprint['budgets'] }) {
    return (
        <>
            <FieldRow label="Max runtime" value={`${budgets.maxWallClockMinutes} min`} />
            <FieldRow label="Max total jobs" value={budgets.maxTotalJobs} />
            <FieldRow label="Max experiments" value={budgets.maxExperiments} />
            <FieldRow label="Max screener jobs" value={budgets.maxScreenerOptimizerJobs} />
            <FieldRow label="Max portfolio BTs" value={budgets.maxPortfolioBacktests} />
            <FieldRow label="Max write actions" value={budgets.maxWriteActions} />
        </>
    );
}

function ConsentSection({ consent }: { consent: Blueprint['consent'] }) {
    return (
        <>
            <div className="flex flex-wrap gap-2 mb-1.5">
                <BooleanDot label="Creates" value={consent.allowCreates} />
                <BooleanDot label="Updates" value={consent.allowUpdates} />
                <BooleanDot label="Deletes" value={consent.allowDeletes} />
            </div>
            <div>
                <span className="text-zinc-500">Scope tags:</span>
                <ChipList items={consent.scopeTags} />
            </div>
        </>
    );
}

function PreflightSection({ preflight }: { preflight: PreflightOutput }) {
    if (!preflight.success) return <div className="text-red-400">Preflight failed</div>;

    return (
        <>
            <div className={`font-medium ${preflight.okToLock ? 'text-green-400' : 'text-red-400'}`}>
                {preflight.okToLock ? 'All checks passed' : 'Blockers found'}
            </div>
            {preflight.blockers.length > 0 && (
                <div className="mt-1">
                    {preflight.blockers.map((b, i) => (
                        <div className="flex items-start gap-1.5 py-0.5 text-red-300" key={i}>
                            <span className="shrink-0">\u2718</span>
                            <span>{b.message}</span>
                        </div>
                    ))}
                </div>
            )}
            {preflight.warnings.length > 0 && (
                <div className="mt-1">
                    {preflight.warnings.map((w, i) => (
                        <div className="flex items-start gap-1.5 py-0.5 text-yellow-300" key={i}>
                            <span className="shrink-0">\u26A0</span>
                            <span>{w.message}</span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export function AutopilotPanel({ planId, runData, onNavigateToSession }: AutopilotPanelProps) {
    const queryClient = useQueryClient();
    const [showRawJson, setShowRawJson] = useState(false);
    const [isLockConfirmOpen, setIsLockConfirmOpen] = useState(false);
    const [isLaunchConfirmOpen, setIsLaunchConfirmOpen] = useState(false);
    const [isResumeConfirmOpen, setIsResumeConfirmOpen] = useState(false);

    const isRunActive = runData?.status === 'running';
    const isRunPaused = runData?.status === 'paused';

    const planQuery = useQuery({
        ...trpc.agent.autopilot.getPlan.queryOptions({ planId }),
        refetchInterval: isRunActive ? 5000 : false,
        retry: false
    });

    const plan = planQuery.data?.success ? planQuery.data.plan : null;
    const blueprint = plan?.blueprint ?? null;

    const preflightQuery = useQuery({
        ...trpc.agent.autopilot.preflightBlueprint.queryOptions({ blueprint: blueprint! }),
        enabled: blueprint !== null,
        retry: false
    });

    const invalidatePlanQueries = useCallback(async () => {
        await queryClient.invalidateQueries(trpc.agent.autopilot.getPlan.pathFilter());
        await queryClient.invalidateQueries(trpc.agent.autopilot.listPlans.pathFilter());
    }, [queryClient]);

    const lockPlanMutation = useTrpcMutation(trpc.agent.autopilot.lockPlan.mutationOptions({ onSuccess: invalidatePlanQueries }), {
        fallbackErrorMessage: 'Failed to lock plan'
    });

    const startRunFromPlanMutation = useTrpcMutation(trpc.agent.autopilot.startRunFromPlan.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to start autopilot run'
    });

    const resumeRunMutation = useTrpcMutation(trpc.agent.autopilot.resumeRun.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to resume autopilot run'
    });

    const handleLockPlan = useCallback(() => {
        void lockPlanMutation.mutateAsync({ planId }).then((res) => {
            if (!res.success) return;
            if (res.okToLock) showToast('Plan locked', 'success');
            else showToast('Plan could not be locked (preflight blockers)', 'warning');
        });
    }, [lockPlanMutation, planId]);

    const handleStartRun = useCallback(() => {
        void startRunFromPlanMutation.mutateAsync({ planId }).then(async (res) => {
            if (!res.success) return;

            if (res.okToStart && 'sessionId' in res && typeof res.sessionId === 'number') {
                showToast('Autopilot run queued', 'success');
                await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
                onNavigateToSession(res.sessionId);
            } else {
                showToast('Autopilot run blocked (preflight blockers)', 'warning');
            }
        });
    }, [startRunFromPlanMutation, planId, queryClient, onNavigateToSession]);

    const handleResumeRun = useCallback(() => {
        if (!runData) return;

        void resumeRunMutation.mutateAsync({ autopilotRunId: runData.id }).then(async (res) => {
            if (!res.success) return;

            if (res.okToResume && typeof res.sessionId === 'number') {
                showToast('Autopilot resumed', 'success');
                await queryClient.invalidateQueries(trpc.agent.autopilot.getContextBySessionId.pathFilter());
                await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
                onNavigateToSession(res.sessionId);
            } else {
                showToast(res.reason ?? 'Autopilot run could not be resumed', 'warning');
            }
        });
    }, [runData, resumeRunMutation, queryClient, onNavigateToSession]);

    const planStatus = plan?.status ?? 'draft';

    const displayStatus = useMemo(() => {
        if (runData) return runData.status;
        return planStatus;
    }, [runData, planStatus]);

    if (planQuery.isLoading) {
        return (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 overflow-auto h-[calc(100vh-220px)] min-h-[520px]">
                <div className="text-sm text-zinc-400">Loading plan...</div>
            </div>
        );
    }

    if (!plan || !blueprint) {
        return (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 overflow-auto h-[calc(100vh-220px)] min-h-[520px]">
                <div className="text-sm text-zinc-400">Plan not found</div>
            </div>
        );
    }

    return (
        <div
            className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden flex flex-col h-[calc(100vh-220px)] min-h-[520px]"
            id="autopilot-panel"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800 shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-white truncate">{plan.name}</span>
                </div>
                <Badge pill text={displayStatus} variant={STATUS_BADGE_VARIANT[displayStatus] ?? 'secondary'} />
            </div>

            {/* Run status bar */}
            {runData && (
                <div className="flex items-center gap-3 px-3 py-1.5 border-b border-zinc-800 bg-zinc-800/30 text-xs text-zinc-400 shrink-0">
                    <span>
                        Run #{runData.id} &mdash; {runData.status}
                    </span>
                    {runData.startedAt && <span>Started: {runData.startedAt}</span>}
                    {runData.finishedAt && <span>Finished: {runData.finishedAt}</span>}
                </div>
            )}

            {/* Scrollable sections */}
            <div className="flex-1 overflow-auto">
                <CollapsibleSection defaultOpen title="Objective">
                    <ObjectiveSection objective={blueprint.objective} />
                </CollapsibleSection>

                <CollapsibleSection defaultOpen title="Scope">
                    <ScopeSection scope={blueprint.scope} />
                </CollapsibleSection>

                <CollapsibleSection defaultOpen title="Universe">
                    <UniverseSection universe={blueprint.universe} />
                </CollapsibleSection>

                <CollapsibleSection title="Strategy Design">
                    <StrategyDesignSection design={blueprint.strategyDesign} />
                </CollapsibleSection>

                <CollapsibleSection title="Experiment Plan">
                    <ExperimentPlanSection plan={blueprint.experimentPlan} />
                </CollapsibleSection>

                <CollapsibleSection title="Backtest Plan">
                    <BacktestPlanSection plan={blueprint.backtestPlan} />
                </CollapsibleSection>

                <CollapsibleSection title="Budgets">
                    <BudgetsSection budgets={blueprint.budgets} />
                </CollapsibleSection>

                <CollapsibleSection title="Consent">
                    <ConsentSection consent={blueprint.consent} />
                </CollapsibleSection>

                {preflightQuery.data?.success && (
                    <CollapsibleSection defaultOpen title="Preflight">
                        <PreflightSection preflight={preflightQuery.data} />
                    </CollapsibleSection>
                )}

                {/* Raw JSON escape hatch */}
                <div className="border-b border-zinc-800 last:border-b-0">
                    <button
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-zinc-500 hover:text-zinc-400"
                        type="button"
                        onClick={() => setShowRawJson(!showRawJson)}
                    >
                        <span>View Blueprint JSON</span>
                        <span>{showRawJson ? '\u2212' : '+'}</span>
                    </button>
                    {showRawJson && (
                        <pre className="px-3 pb-3 text-[11px] text-zinc-300 whitespace-pre-wrap break-words max-h-[400px] overflow-auto">
                            {JSON.stringify(blueprint, null, 2)}
                        </pre>
                    )}
                </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 px-3 py-2.5 border-t border-zinc-800 shrink-0">
                {planStatus === 'draft' && (
                    <button
                        className="flex-1 px-3 py-2 rounded-md bg-emerald-700 hover:bg-emerald-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium"
                        disabled={lockPlanMutation.isPending}
                        type="button"
                        onClick={() => {
                            if (preflightQuery.data?.success && !preflightQuery.data.okToLock) {
                                showToast('Plan has preflight blockers — see the Preflight section.', 'warning');
                                return;
                            }

                            setIsLockConfirmOpen(true);
                        }}
                    >
                        {lockPlanMutation.isPending ? 'Locking...' : 'Lock Plan'}
                    </button>
                )}
                {planStatus === 'locked' && !runData && (
                    <button
                        className="flex-1 px-3 py-2 rounded-md bg-fuchsia-700 hover:bg-fuchsia-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium"
                        disabled={startRunFromPlanMutation.isPending}
                        type="button"
                        onClick={() => setIsLaunchConfirmOpen(true)}
                    >
                        {startRunFromPlanMutation.isPending ? 'Starting...' : 'Launch Autopilot'}
                    </button>
                )}
                {isRunPaused && runData && (
                    <button
                        className="flex-1 px-3 py-2 rounded-md bg-blue-700 hover:bg-blue-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium"
                        disabled={resumeRunMutation.isPending}
                        type="button"
                        onClick={() => setIsResumeConfirmOpen(true)}
                    >
                        {resumeRunMutation.isPending ? 'Resuming...' : 'Resume Autopilot'}
                    </button>
                )}
                <button
                    className="px-3 py-2 rounded-md bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-medium"
                    type="button"
                    onClick={() => {
                        void planQuery.refetch();
                    }}
                >
                    Refresh
                </button>
            </div>

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText={lockPlanMutation.isPending ? 'Locking...' : 'Lock plan'}
                confirmVariant="primary"
                isConfirmLoading={lockPlanMutation.isPending}
                isOpen={isLockConfirmOpen}
                message={
                    <div className="space-y-2">
                        <div>Locking will freeze the blueprint so Autopilot can run exactly what you planned.</div>
                        {preflightQuery.data?.success && preflightQuery.data.warnings.length > 0 && (
                            <div className="text-sm text-yellow-300">
                                Warnings: {preflightQuery.data.warnings.length} (review in Preflight)
                            </div>
                        )}
                    </div>
                }
                title="Lock Autopilot plan?"
                onCancel={() => setIsLockConfirmOpen(false)}
                onConfirm={() => {
                    setIsLockConfirmOpen(false);
                    handleLockPlan();
                }}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText={startRunFromPlanMutation.isPending ? 'Starting...' : 'Launch autopilot'}
                confirmVariant="warning"
                isConfirmLoading={startRunFromPlanMutation.isPending}
                isOpen={isLaunchConfirmOpen}
                message={
                    <div className="space-y-2">
                        <div>This will start a new Autopilot execution chat and run the locked plan.</div>
                        <div className="text-sm text-zinc-400">Budgets and consent are enforced by the plan.</div>
                    </div>
                }
                title="Launch Autopilot?"
                onCancel={() => setIsLaunchConfirmOpen(false)}
                onConfirm={() => {
                    setIsLaunchConfirmOpen(false);
                    handleStartRun();
                }}
            />

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText={resumeRunMutation.isPending ? 'Resuming...' : 'Resume autopilot'}
                confirmVariant="warning"
                isConfirmLoading={resumeRunMutation.isPending}
                isOpen={isResumeConfirmOpen}
                message={
                    <div className="space-y-2">
                        <div>This will resume the paused Autopilot execution chat and continue from the last checkpoint.</div>
                        <div className="text-sm text-zinc-400">
                            Autopilot will keep running until it completes or hits a budget/turn limit.
                        </div>
                    </div>
                }
                title="Resume Autopilot?"
                onCancel={() => setIsResumeConfirmOpen(false)}
                onConfirm={() => {
                    setIsResumeConfirmOpen(false);
                    handleResumeRun();
                }}
            />
        </div>
    );
}
